import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  CtBinaryOperator,
  CtButtonConfiguration,
  CtModelConfiguration,
  CTModelDatatableFilter,
  CtModelDatatableOperators,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  MAT_RAISED_PRIMARY,
  SnackbarService
} from '@ctsolution/ct-framework';
import { CtWebapiGenericResponse, CtWebapiService, DataRequest } from '@ctsolution/ct-webapi';
import { Observable } from 'rxjs';

import { CtLookupItem } from '@ctsolution/ct-framework/lib/_containers/ct-model/lib/ct-backoffice/lookups.ct-backoffice';
import { LeadInformationFormService } from './lead-information-form.service';
import { LeadInformationRouteData } from '../../../../_core/route-data/lead-information.route-data';
import { LeadInformation } from './lead-information';
import { CTConfigurationModel } from '../../../../_core/classes/ct-configuration-model';
import { CtManagerService } from '../../../../_core/lib/ct-manager.service';
import { DEFAULT_DATATABLE_CONFIGURATION } from '../../../../_core/constants';

/**
 * Componente per la gestione dei campi aggiuntivi del lead tramite LeadInformation.
 *
 * Segue il pattern `CompanyInformationComponent` in modalità CUSTOM:
 * 1. Il form viene costruito puntando il controller a un JSON statico in assets/demo/
 *    che simula la risposta dell'API con la struttura `Result.CTFieldInfos`
 * 2. I campi visibili vengono filtrati via `fieldsCustomTemplates` in base alla
 *    configurazione per-client (`leadInformationFields`)
 * 3. I valori esistenti vengono caricati con una POST separata al controller reale
 *    e iniettati nel form via `setDataSource` + `_prefillForm`
 *
 * Visibilità e campi da mostrare configurabili per-client tramite:
 * - `CTConfigurationModel.leadInformationEnabled`
 * - `CTConfigurationModel.leadInformationFields` (array di valori `ContactInformationTypeVO`)
 */
@Component({
  selector: 'app-lead-information-form',
  templateUrl: './lead-information-form.component.html'
})
export class LeadInformationFormComponent implements OnInit {

  // ─── Input ────────────────────────────────────────────────────────────────

  /** Configurazione del model lead padre (necessaria per recuperare l'OID del lead). */
  @Input() masterConfiguration: CtModelConfiguration<any> | null = null;

  // ─── Stato ────────────────────────────────────────────────────────────────

  /** Configurazione del form dinamico — null finché il setup non è completato. */
  modelConfiguration: CtModelConfiguration<any> | null = null;

  /**
   * Configurazione per-client di questo componente.
   * Valorizzata tramite `getComponentOverride` in `ngOnInit`.
   */
  leadInformationConfiguration: CTConfigurationModel = CTConfigurationModel.create();

  submitButton: CtButtonConfiguration = CtButtonConfiguration
    .create()
    .setLabel('CT_PAGE.CT_FORM.save')
    .setClass('ct-model-submit-button')
    .setAction(() => this.onSubmit())
    .setMatherialOptions(MAT_RAISED_PRIMARY);

  // ─────────────────────────────────────────────────────────────────────────

  constructor(
    private managerService: CtManagerService,
    private leadInformationFormService: LeadInformationFormService,
    private _model: CtModelService<any>,
    private _webapi: CtWebapiService,
    private _snackbar: SnackbarService,
    private _cdr: ChangeDetectorRef
  ) {}

  // ─── Lifecycle ────────────────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {

    // Carica l'override per-client prima di procedere
    await this.managerService.getComponentOverride(
      'lead',
      'leadInformationConfiguration',
      this.leadInformationConfiguration
    );

    // Esce se manca l'OID del lead (form non ancora salvato) o la funzionalità è disabilitata
    if (
      !this.masterConfiguration?.RouteData?.id ||
      !this.leadInformationConfiguration.leadInformationEnabled
    ) {
      return;
    }

    this._setup();

  }

  // ─── Setup ────────────────────────────────────────────────────────────────

  /**
   * Costruisce il form seguendo il pattern CUSTOM di CompanyInformation:
   * - Il controller punta al JSON template in assets/demo/ (formato Result.CTFieldInfos)
   * - I campi non abilitati vengono nascosti con `fieldsCustomTemplates`
   * - Una POST separata al controller reale carica i valori esistenti
   */
  private _setup(): void {

    this.leadInformationFormService.getLeadInformationTypes().subscribe(allTypes => {

      // Calcola quali Description devono essere NASCOSTI (tutti tranne quelli abilitati)
      const enabledCodes = this.leadInformationConfiguration.leadInformationFields.map(String);
      const enabledDescriptions = allTypes
        .filter(t => enabledCodes.includes(String(t.Code)))
        .map(t => t.Description)
        .filter((d): d is string => !!d);

      if (!enabledDescriptions.length) {
        console.warn('LeadInformationFormComponent: nessun campo abilitato in configurazione.');
        return;
      }

      // Tutti i campi non abilitati vengono nascosti via fieldsCustomTemplates.
      // Filtriamo esplicitamente i tipi senza Description (string | null → string)
      const hiddenFields = allTypes
        .filter(t => !enabledCodes.includes(String(t.Code)) && t.Description != null)
        .map(t => ({ prop: t.Description as string, visible: false }));

      const configuration = CtModelConfiguration.create()
        .setRouteData(
          CtModelRouteData.create()
            // Il controller punta al JSON statico — il framework lo usa per costruire il form
            .setController(`${window.location.origin}/assets/demo/lead-information-model.json`)
            .setModelType(CtModelType.FORM)
        )
        .setDefaultActionsEnabled(false)
        .setCTDatatableConfiguration(DEFAULT_DATATABLE_CONFIGURATION().setScrollbarH(false))
        // Nasconde tutti i campi non configurati per questo client
        .setFieldsCustomTemplates(hiddenFields);

      this.modelConfiguration = configuration;

      // Notifica Angular del cambio avvenuto in contesto asincrono (evita NG0100)
      this._cdr.detectChanges();

      // Carica i valori esistenti e pre-compila il form
      this._loadExistingValues(allTypes, enabledDescriptions);

    });

  }

  // ─── Caricamento valori esistenti ─────────────────────────────────────────

  /**
   * Recupera i record LeadInformation esistenti per questo lead tramite POST
   * al controller reale, poi pre-compila il form.
   *
   * @param allTypes  Tutti i tipi del JSON (serve per il mapping Code ↔ Description)
   * @param enabledDescriptions  Le Description abilitate (per sapere quali campi compilare)
   */
  private _loadExistingValues(allTypes: CtLookupItem[], enabledDescriptions: string[]): void {

    const leadOid = this.masterConfiguration?.RouteData?.id;
    if (!leadOid) return;

    const body: CtModelDatatableOperators = CtModelDatatableOperators.create().setFilters([
      CTModelDatatableFilter.create()
        .setField('Lead.Oid')
        .setValue(leadOid)
        .setOperatorType(CtBinaryOperator.Equal)
    ]);

    const request: DataRequest = new DataRequest()
      .setController([LeadInformationRouteData().controller!])
      .setAction('List')
      .setQueryParams({ hideSpinner: true })
      .setBody(body);

    this._webapi.post<any>(request).subscribe({
      next: (result: CtWebapiGenericResponse<any>) => {
        const dataSource: LeadInformation[] = result?.Result?.DataSource ?? [];
        // Salva il datasource nella configuration per il submit (update vs insert)
        this.modelConfiguration?.setDataSource(dataSource);
        // Pre-compila il form dopo che il framework ha inizializzato i controlli
        setTimeout(() => this._prefillForm(dataSource, allTypes), 100);
      },
      error: err => console.error('LeadInformationFormComponent: errore caricamento valori:', err)
    });

  }

  /**
   * Pre-compila il form con i valori già presenti sul backend.
   * Identico al pattern `CompanyInformationComponent.setupFormValue()`.
   *
   * @param dataSource  Record LeadInformation esistenti
   * @param allTypes    Mapping Code ↔ Description dal JSON dei tipi
   */
  private _prefillForm(dataSource: LeadInformation[], allTypes: CtLookupItem[]): void {

    if (!this.modelConfiguration?.form) return;

    const formValue = this.modelConfiguration.form.value;

    Object.keys(formValue).forEach(key => {

      // Trova il tipo corrispondente alla chiave del form (Description → Code)
      const typeEntry = allTypes.find(
        t => t.Description?.toLowerCase() === key.toLowerCase()
      );
      if (!typeEntry) return;

      // Trova il record esistente con quel Type numerico (Type è ora number nel modello)
      const existing = dataSource.find(
        el => el.Type === parseInt(String(typeEntry.Code), 10)
      );

      if (existing) {
        this.modelConfiguration?.form?.get(key)?.setValue(existing.Content);
      }

    });

  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  /**
   * Salvataggio standalone: valida il form e salva tutti i campi.
   * Chiamato dal pulsante "Salva" interno al componente.
   */
  onSubmit(): void {

    if (!this.modelConfiguration?.form) return;

    this.modelConfiguration.form.markAllAsTouched();

    if (!this.modelConfiguration.form.valid) return;

    this._saveAll().subscribe({
      next: () => {
        this._snackbar.onSuccess();
        // Ricarica i valori per aggiornare il datasource (necessario per i successivi salvataggi)
        this.leadInformationFormService.getLeadInformationTypes().subscribe(allTypes => {
          const enabledCodes = this.leadInformationConfiguration.leadInformationFields.map(String);
          const enabledDescriptions = allTypes
            .filter(t => enabledCodes.includes(String(t.Code)))
            .map(t => t.Description)
            .filter((d): d is string => !!d);
          this._loadExistingValues(allTypes, enabledDescriptions);
        });
      },
      error: err => console.error('LeadInformationFormComponent: errore durante il salvataggio:', err)
    });

  }

  /**
   * Logica di salvataggio: fa PUT per ogni campo del form come record separato.
   * Condivisa tra `onSubmit` (standalone) e `submitForm` (sincronizzato con parent).
   */
  private _saveAll(): Observable<any> {

    return new Observable(observer => {

      if (!this.modelConfiguration?.form) {
        observer.next({ success: true });
        observer.complete();
        return;
      }

      const value = this.modelConfiguration.form.value;
      const keys = Object.keys(value);

      if (!keys.length) {
        observer.next({ success: true });
        observer.complete();
        return;
      }

      this.leadInformationFormService.getLeadInformationTypes().subscribe({
        next: allTypes => {

          let completed = 0;
          const total = keys.length;
          let hasError = false;

          keys.forEach(key => {

            // Trova il tipo corrispondente alla chiave del form
            const typeEntry = allTypes.find(
              t => t.Description?.toLowerCase() === key.toLowerCase()
            );

            if (!typeEntry) {
              // Campo non mappato nel JSON dei tipi: considera completato
              completed++;
              if (completed === total && !hasError) {
                observer.next({ success: true });
                observer.complete();
              }
              return;
            }

            const leadInformation = LeadInformation.create()
              .setLeadOid(this.masterConfiguration?.RouteData?.id ?? -1)
              .setContent(value[key] ?? '')
              .setType(typeEntry.Code);

            const configuration = CtModelConfiguration.create()
              .setRouteData(
                CtModelRouteData.create()
                  .setController(LeadInformationRouteData().controller!)
              );

            // Se esiste già un record con quel Type, usa il suo Oid per l'update (PUT)
            const existing: LeadInformation | undefined = this.modelConfiguration?.DataSource?.find(
              (el: LeadInformation) => el.Type != null && String(el.Type) === String(typeEntry.Code)
            );

            if (existing) {
              configuration.RouteData?.setId(existing.Oid ?? -1);
            }

            this._model.putInfos(configuration, leadInformation)?.subscribe({
              next: () => {
                completed++;
                if (completed === total && !hasError) {
                  observer.next({ success: true });
                  observer.complete();
                }
              },
              error: err => {
                hasError = true;
                console.error(`LeadInformationFormComponent: errore salvataggio '${key}':`, err);
                observer.error(err);
              }
            });

          });

        },
        error: err => {
          console.error('LeadInformationFormComponent: errore recupero tipi:', err);
          observer.error(err);
        }
      });

    });

  }

  /**
   * Metodo esposto per il submit sincronizzato dal parent tramite forkJoin.
   * Predisposto per future integrazioni — attualmente il componente è standalone.
   */
  submitForm(): Observable<any> {
    return this._saveAll();
  }

}
